/**
 * The screener on a touch screen (owner, 2026-10-14; `docs/features/mobile.md`,
 * "The screener on a touch screen"): **what is applied reads at the top, what
 * changes it sits at the bottom**, by the thumb.
 *
 * - **Top row, always one row.** Universes on the left, joined by the chosen OR /
 *   AND; conditions on the right. Each side shows what fits, then `+N▾` — a
 *   dropdown with every universe and its count, or every condition and the
 *   result count.
 * - **Bottom row** (portrait): search, 🌐 universes, ⚑ conditions, ⊘ clear
 *   filters. In landscape it is a **rail on the right, icons only**.
 * - **Sheets**: Universe (market, the picked set, OR / AND, the types as tick
 *   lists, and ⋯ for Columns / Refresh / Single-Multi) and Conditions (pick
 *   only — the desktop's `SavedFilterPicker` inline, rows made finger-sized).
 *
 * The rules a tick follows are `lib/touch-screener.ts`, tested there; nothing
 * here decides what a selection means.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { SavedScreenerFilter } from "@qufolio/model";
import { SavedFilterPicker } from "@qufolio/screener-grid";

import type { SymbolGroup, SymbolGroupType } from "../api/symbols";
import type { ListTools } from "./ScreenerBar";
import type { GroupSelection } from "../lib/group-selection";
import { marketByCode } from "../lib/market";
import {
  appliedUniverses, chipsThatFit, isTicked, mergeAcrossMarkets, removeUniverse, tickUniverse,
} from "../lib/touch-screener";

export interface ScreenerTouchBarsProps {
  types: readonly SymbolGroupType[];
  groupsByType: ReadonlyMap<string, readonly SymbolGroup[]>;
  selection: GroupSelection;
  onSelectionChange: (next: GroupSelection) => void;
  /** Rows in the universe, before conditions; null before anything loads. */
  total: number | null;
  /** Rows the conditions keep; null when nothing is filtered out. */
  matched: number | null;
  filters: readonly SavedScreenerFilter[];
  categoryOrder?: readonly string[];
  selectedIds: readonly string[];
  onFilterIds: (ids: string[]) => void;
  filtersOn: boolean;
  onFiltersToggle: () => void;
  onRefresh: () => void;
  refreshing: boolean;
  onColumns?: () => void;
  /** ⋯'s import and export — the wide bar's entries, the same rules. */
  listTools?: ListTools;
  query: string;
  onQuery: (query: string) => void;
  /** Landscape: the controls become a rail on the right, icons only. */
  rail: boolean;
  /**
   * An iPad: the room used — each universe's count inline in the top row, a
   * short label beside each control, and the sheets as a centred panel.
   */
  tablet?: boolean;
  /** The list. */
  children: ReactNode;
}

type Open = null | "universes" | "conditions" | "universeSheet" | "conditionSheet";

export function ScreenerTouchBars(props: ScreenerTouchBarsProps) {
  const { selection, groupsByType, filters, selectedIds, filtersOn, rail, total, matched, tablet = false } = props;
  const [open, setOpen] = useState<Open>(null);
  const [searching, setSearching] = useState(false);

  const universes = useMemo(() => appliedUniverses(selection, groupsByType), [selection, groupsByType]);
  const conditions = useMemo(
    () => filters.filter((f) => selectedIds.includes(f.id)),
    [filters, selectedIds],
  );
  const joiner = selection.view === "multi" ? selection.combine.toUpperCase() : "";
  const after = filtersOn && conditions.length > 0 ? (matched ?? total) : total;

  const top = (
    <div style={TOP} data-testid="touch-applied">
      <FitRow
        label="Universes"
        items={universes.map((u, i) => ({
          key: u.code,
          text: u.name,
          joiner: i > 0 ? joiner : "",
          ...(tablet ? { count: u.count } : {}),
        }))}
        outlined
        onMore={() => setOpen(open === "universes" ? null : "universes")}
        empty="No universe"
        grow={1}
      />
      {conditions.length > 0 && (
        <>
          <span style={{ width: 1, alignSelf: "stretch", background: "var(--qf-border)" }} />
          <FitRow
            label="Conditions"
            items={conditions.map((c) => ({ key: c.id, text: c.name || "Untitled" }))}
            dim={!filtersOn}
            onMore={() => setOpen(open === "conditions" ? null : "conditions")}
            empty=""
            grow={1}
          />
        </>
      )}
    </div>
  );

  const controls = (
    <div style={rail ? { ...RAIL, ...(tablet ? { width: 124, alignItems: "stretch" } : null) } : BOTTOM} data-testid="touch-controls">
      {rail ? (
        <RailKey label="Search" text={tablet ? "Search" : undefined} active={searching} onClick={() => setSearching((v) => !v)}>
          🔍
        </RailKey>
      ) : (
        <SearchBox query={props.query} onQuery={props.onQuery} />
      )}
      <RailKey
        label="Universes"
        text={tablet ? `Universe ${universes.length}` : undefined}
        badge={tablet ? undefined : universes.length}
        onClick={() => setOpen("universeSheet")}
      >
        🌐
      </RailKey>
      <RailKey
        label="Conditions"
        text={tablet ? `Conditions ${conditions.length}` : undefined}
        badge={tablet ? undefined : conditions.length}
        onClick={() => setOpen("conditionSheet")}
      >
        ⚑
      </RailKey>
      <RailKey
        text={tablet ? "Clear" : undefined}
        label={filtersOn ? "Filters on — turn off" : "Filters off — turn on"}
        active={filtersOn && conditions.length > 0}
        disabled={conditions.length === 0}
        onClick={props.onFiltersToggle}
      >
        ⊘
      </RailKey>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, position: "relative" }}>
      {top}
      {rail && (searching || props.query) && (
        <div style={{ padding: "6px 8px", borderBottom: "1px solid var(--qf-border)" }}>
          <SearchBox query={props.query} onQuery={props.onQuery} autoFocus={searching} />
        </div>
      )}
      <div style={{ flex: "1 1 auto", minHeight: 0, display: "flex" }}>
        <div style={{ flex: "1 1 auto", minWidth: 0, minHeight: 0 }}>{props.children}</div>
        {rail && controls}
      </div>
      {!rail && controls}

      {open === "universes" && (
        <Dropdown onClose={() => setOpen(null)} side="left">
          <div style={DROP_HEAD}>
            <span>
              {joiner ? `${joiner} · ` : ""}
              {universes.length} universe{universes.length === 1 ? "" : "s"}
            </span>
            <span>{total ?? "—"}</span>
          </div>
          {universes.map((u) => (
            <div key={u.code} style={DROP_ROW}>
              <span>{u.name}</span>
              <span style={MUTED}>{u.count.toLocaleString()}</span>
            </div>
          ))}
          <button type="button" style={DROP_LINK} onClick={() => setOpen("universeSheet")}>
            Edit universes…
          </button>
        </Dropdown>
      )}
      {open === "conditions" && (
        <Dropdown onClose={() => setOpen(null)} side="right">
          <div style={DROP_HEAD}>
            <span>
              {total ?? "—"} → {after ?? "—"} rows
            </span>
            {!filtersOn && <span style={MUTED}>off</span>}
          </div>
          {conditions.map((c) => (
            <div key={c.id} style={DROP_ROW}>
              <span>{c.name || "Untitled"}</span>
              <button
                type="button"
                aria-label={`Remove ${c.name}`}
                style={X}
                onClick={() => props.onFilterIds(selectedIds.filter((id) => id !== c.id))}
              >
                ✕
              </button>
            </div>
          ))}
          <button type="button" style={DROP_LINK} onClick={() => setOpen("conditionSheet")}>
            Edit conditions…
          </button>
        </Dropdown>
      )}
      {open === "universeSheet" && <UniverseSheet {...props} onClose={() => setOpen(null)} />}
      {open === "conditionSheet" && (
        <Sheet title="Conditions" centred={tablet} onClose={() => setOpen(null)} extra={
          <button
            type="button"
            aria-pressed={filtersOn}
            onClick={props.onFiltersToggle}
            disabled={conditions.length === 0}
            style={{ ...PILL, ...(filtersOn && conditions.length > 0 ? PILL_ON : null) }}
          >
            Filter {filtersOn ? "On" : "Off"}
          </button>
        } footer={
          <>
            <span>
              {total ?? "—"} → {after ?? "—"} rows
            </span>
            <button type="button" style={{ ...PILL, ...PILL_ON }} onClick={() => setOpen(null)}>
              Done
            </button>
          </>
        }>
          <div className="qf-touch-pick">
            <style>{TOUCH_PICK_CSS}</style>
            <SavedFilterPicker
              inline
              filters={filters}
              categoryOrder={props.categoryOrder}
              selected={selectedIds}
              onChange={(ids) => props.onFilterIds(ids)}
            />
          </div>
        </Sheet>
      )}
    </div>
  );
}

/**
 * One side of the top row: as many chips as fit, then `+N▾`. Widths come from an
 * off-screen ruler holding every chip — measuring the visible row would only
 * ever see what it already shows (the desktop bar's lesson, `GroupBar`).
 */
function FitRow({
  label, items, outlined, dim, onMore, empty, grow,
}: {
  label: string;
  items: { key: string; text: string; joiner?: string; count?: number }[];
  outlined?: boolean;
  dim?: boolean;
  onMore: () => void;
  empty: string;
  grow: number;
}) {
  const box = useRef<HTMLDivElement | null>(null);
  const ruler = useRef<HTMLDivElement | null>(null);
  const plus = useRef<HTMLButtonElement | null>(null);
  const [shown, setShown] = useState(items.length);
  const signature = items.map((i) => i.key + i.text + (i.joiner ?? "") + (i.count ?? "")).join("|");

  useLayoutEffect(() => {
    const measure = () => {
      if (!box.current || !ruler.current) return;
      const widths = Array.from(ruler.current.children).map((c) => (c as HTMLElement).offsetWidth);
      const avail = box.current.clientWidth;
      // jsdom lays nothing out: no width means show everything.
      setShown(avail > 0 ? chipsThatFit(widths, avail, 4, (plus.current?.offsetWidth ?? 36) || 36) : items.length);
    };
    measure();
    if (typeof ResizeObserver === "undefined" || !box.current) return;
    const ro = new ResizeObserver(measure);
    ro.observe(box.current);
    return () => ro.disconnect();
  }, [signature]);

  const chip = (it: { key: string; text: string; joiner?: string; count?: number }) => (
    <span key={it.key} style={{ display: "inline-flex", alignItems: "center", gap: 4, flex: "0 0 auto" }}>
      {it.joiner ? <span style={{ fontSize: 10, color: "var(--qf-text-muted)", fontWeight: 700 }}>{it.joiner}</span> : null}
      <span style={{ ...(outlined ? CHIP_OUT : CHIP_FILL), ...(dim ? { opacity: 0.5, textDecoration: "line-through" } : null) }}>
        {it.text}
        {it.count !== undefined ? <span style={{ marginLeft: 5, color: "var(--qf-text-muted)" }}>{it.count.toLocaleString()}</span> : null}
      </span>
    </span>
  );
  const hidden = items.length - shown;
  return (
    <div style={{ flex: `${grow} 1 0`, minWidth: 0, display: "flex", alignItems: "center", gap: 4, position: "relative" }} aria-label={label}>
      <div ref={box} style={{ flex: "1 1 auto", minWidth: 0, display: "flex", gap: 4, overflow: "hidden", alignItems: "center" }}>
        {items.length === 0 ? <span style={MUTED}>{empty}</span> : items.slice(0, shown).map(chip)}
      </div>
      {items.length > 0 && (
        <button ref={plus} type="button" onClick={onMore} style={PLUS} aria-label={`All ${label.toLowerCase()}`}>
          {hidden > 0 ? `+${hidden}` : ""}▾
        </button>
      )}
      <div ref={ruler} aria-hidden="true" style={{ position: "absolute", left: -10000, top: 0, display: "flex", gap: 4, visibility: "hidden" }}>
        {items.map(chip)}
      </div>
    </div>
  );
}

function SearchBox({ query, onQuery, autoFocus }: { query: string; onQuery: (q: string) => void; autoFocus?: boolean }) {
  return (
    <div style={{ flex: "1 1 auto", minWidth: 0, position: "relative", display: "flex" }}>
      <input
        type="search"
        aria-label="Search symbol or name"
        placeholder="Search symbol or name"
        value={query}
        autoFocus={autoFocus}
        onChange={(e) => onQuery(e.target.value)}
        style={SEARCH}
      />
    </div>
  );
}

function RailKey({
  label, text, badge, active, disabled, onClick, children,
}: {
  label: string;
  /** A short label beside the icon — the iPad's form. */
  text?: string;
  badge?: number;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      style={{
        ...KEY,
        // Labelled (the iPad): a smaller icon and tighter spacing, so "Conditions
        // 4" fits the 124px rail; the label is cut with an ellipsis rather than
        // ever running over the key's border (owner, 2026-10-14).
        ...(text ? { width: "auto", minWidth: 0, padding: "0 6px", gap: 4, justifyContent: "flex-start", overflow: "hidden" } : null),
        ...(active ? KEY_ON : null),
        ...(disabled ? { opacity: 0.4 } : null),
      }}
    >
      <span style={{ fontSize: text ? 14 : 17, flex: "0 0 auto" }}>{children}</span>
      {text ? (
        <span style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{text}</span>
      ) : null}
      {badge ? <span style={BADGE}>{badge}</span> : null}
    </button>
  );
}

function Dropdown({ onClose, side, children }: { onClose: () => void; side: "left" | "right"; children: ReactNode }) {
  return (
    <>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, zIndex: 40 }} />
      <div role="menu" style={{ ...DROP, [side]: 8 }}>{children}</div>
    </>
  );
}

function Sheet({
  title, onClose, extra, footer, centred = false, children,
}: {
  title: string;
  /** The iPad's form: a panel in the middle of the screen, not a sheet from the bottom. */
  centred?: boolean;
  onClose: () => void;
  extra?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  useEffect(() => {
    const key = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);
  return (
    <div style={centred ? { ...SCRIM, alignItems: "center" } : SCRIM} onClick={onClose}>
      <div
        role="dialog"
        aria-label={title}
        data-centred={centred ? "" : undefined}
        style={centred ? { ...SHEET, borderRadius: 14, maxHeight: "80%", paddingBottom: 0 } : SHEET}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={SHEET_HEAD}>
          <strong style={{ fontSize: 16 }}>{title}</strong>
          <span style={{ flex: 1 }} />
          {extra}
          <button type="button" aria-label="Close" onClick={onClose} style={X}>
            ✕
          </button>
        </div>
        <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", padding: "0 14px 10px" }}>{children}</div>
        {footer && <div style={SHEET_FOOT}>{footer}</div>}
      </div>
    </div>
  );
}

function UniverseSheet(props: ScreenerTouchBarsProps & { onClose: () => void }) {
  const { selection, onSelectionChange, groupsByType, types, onClose } = props;
  const [menu, setMenu] = useState(false);
  const [openTypes, setOpenTypes] = useState<ReadonlySet<string>>(
    () => new Set(selection.view === "single" ? [selection.groupType] : selection.picked.map((p) => p.groupType)),
  );
  const universes = appliedUniverses(selection, groupsByType);
  const markets = useMemo(() => {
    const seen = new Set<string>();
    for (const list of groupsByType.values()) for (const g of list) if (g.market && !g.locked) seen.add(g.market);
    return [...seen].sort();
  }, [groupsByType]);
  const inMarket = (g: SymbolGroup) =>
    selection.markets.length === 0 || g.market == null || selection.markets.includes(g.market);

  return (
    <Sheet
      title="Universe"
      centred={props.tablet}
      onClose={onClose}
      extra={
        <span style={{ position: "relative" }}>
          <button type="button" aria-label="More" style={X} onClick={() => setMenu((v) => !v)}>
            ⋯
          </button>
          {menu && (
            <div role="menu" style={{ ...DROP, top: 36, right: 0, bottom: "auto" }}>
              {props.onColumns && (
                <button type="button" style={DROP_LINK} onClick={() => { setMenu(false); onClose(); props.onColumns?.(); }}>
                  Columns
                </button>
              )}
              {props.listTools?.onPaste && (
                <button type="button" style={DROP_LINK} onClick={() => { setMenu(false); onClose(); props.listTools?.onPaste?.(); }}>
                  Paste from clipboard
                </button>
              )}
              {props.listTools?.onImport && (
                <button type="button" style={DROP_LINK} onClick={() => { setMenu(false); onClose(); props.listTools?.onImport?.(); }}>
                  Import symbols…
                </button>
              )}
              {props.listTools?.onNewList && (
                <button type="button" style={DROP_LINK} onClick={() => { setMenu(false); onClose(); props.listTools?.onNewList?.(); }}>
                  New list from screen…
                </button>
              )}
              {props.listTools?.onExport && (
                <button type="button" style={DROP_LINK} onClick={() => { setMenu(false); onClose(); props.listTools?.onExport?.(); }}>
                  Export symbols…
                </button>
              )}
              <button type="button" style={DROP_LINK} onClick={() => { setMenu(false); props.onRefresh(); }}>
                {props.refreshing ? "Refreshing…" : "Refresh"}
              </button>
              <button
                type="button"
                style={DROP_LINK}
                onClick={() => {
                  setMenu(false);
                  onSelectionChange({ ...selection, view: selection.view === "multi" ? "single" : "multi" });
                }}
              >
                {selection.view === "multi" ? "Single select" : "Multi select"}
              </button>
            </div>
          )}
        </span>
      }
    >
      {markets.length > 1 && (
        <div style={SECTION}>
          <span style={SECTION_K}>Market</span>
          <button
            type="button"
            style={{ ...PILL, ...(selection.markets.length === 0 ? PILL_ON : null) }}
            onClick={() => onSelectionChange({ ...selection, markets: [] })}
          >
            All
          </button>
          {markets.map((m) => {
            const on = selection.markets.includes(m);
            return (
              <button
                key={m}
                type="button"
                style={{ ...PILL, ...(on ? PILL_ON : null) }}
                onClick={() =>
                  onSelectionChange({
                    ...selection,
                    markets: on ? selection.markets.filter((x) => x !== m) : [...selection.markets, m],
                  })
                }
              >
                {marketByCode(m).label}
              </button>
            );
          })}
        </div>
      )}
      {universes.length > 0 && (
        <div style={{ ...SECTION, flexWrap: "wrap" }}>
          <span style={SECTION_K}>Picked</span>
          {universes.map((u) => (
            <span key={u.code} style={{ ...CHIP_OUT, display: "inline-flex", alignItems: "center", gap: 6 }}>
              {u.name} <span style={MUTED}>{u.count.toLocaleString()}</span>
              <button
                type="button"
                aria-label={`Remove ${u.name}`}
                style={{ ...X, width: 24, height: 24 }}
                onClick={() => onSelectionChange(removeUniverse(selection, u.groupType, u.code))}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      {selection.view === "multi" && (
        <div style={SECTION}>
          <span style={SECTION_K}>Combine</span>
          {(["or", "and"] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              aria-pressed={selection.combine === mode}
              style={{ ...PILL, ...(selection.combine === mode ? PILL_ON : null) }}
              onClick={() => onSelectionChange({ ...selection, combine: mode })}
            >
              {mode.toUpperCase()}
            </button>
          ))}
        </div>
      )}
      {types.map((t) => {
        const list = mergeAcrossMarkets((groupsByType.get(t.groupType) ?? []).filter(inMarket));
        if (list.length === 0) return null;
        const isOpen = openTypes.has(t.groupType);
        return (
          <div key={t.groupType}>
            <button
              type="button"
              aria-expanded={isOpen}
              style={FOLD}
              onClick={() =>
                setOpenTypes((was) => {
                  const next = new Set(was);
                  if (next.has(t.groupType)) next.delete(t.groupType);
                  else next.add(t.groupType);
                  return next;
                })
              }
            >
              <span style={{ width: 16 }}>{isOpen ? "▾" : "▸"}</span>
              {t.label}
              <span style={{ ...MUTED, marginLeft: "auto", fontWeight: 400 }}>{list.length}</span>
            </button>
            {isOpen &&
              list.map((g) => {
                const on = isTicked(selection, g.groupType, g.codes);
                const locked = g.locked === true;
                return (
                  <button
                    key={g.codes.join(",")}
                    type="button"
                    role="menuitemcheckbox"
                    aria-checked={on}
                    disabled={locked}
                    style={{ ...TICK, ...(on ? { background: "var(--qf-selection)" } : null), ...(locked ? { opacity: 0.5 } : null) }}
                    onClick={() => onSelectionChange(tickUniverse(selection, g.groupType, g.codes))}
                  >
                    <span style={{ width: 22, fontSize: 16 }}>{locked ? "🔒" : on ? "☑" : "☐"}</span>
                    <span style={{ flex: 1, textAlign: "left" }}>{g.name}</span>
                    <span style={MUTED}>{g.memberCount.toLocaleString()}</span>
                  </button>
                );
              })}
          </div>
        );
      })}
    </Sheet>
  );
}

/**
 * The desktop picker's rows, made finger-sized inside the sheet: 44px rows and
 * larger type, without a second picker that would drift from the first.
 */
const TOUCH_PICK_CSS = `
.qf-touch-pick [role="group"] { width: 100% !important; max-width: none !important; border: 0 !important; box-shadow: none !important; }
.qf-touch-pick label { min-height: 44px; font-size: 15px !important; align-items: center; }
.qf-touch-pick label span { font-size: 15px !important; }
.qf-touch-pick input[type="checkbox"] { width: 20px; height: 20px; }
.qf-touch-pick input[type="search"], .qf-touch-pick input[type="text"] { font-size: 16px !important; min-height: 36px; }
.qf-touch-pick button { min-height: 32px; }
`;

const TOP: CSSProperties = { display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", minHeight: 40, borderBottom: "1px solid var(--qf-border)", background: "var(--qf-surface)", flex: "0 0 auto" };
const BOTTOM: CSSProperties = { display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", borderTop: "1px solid var(--qf-border)", background: "var(--qf-surface)", flex: "0 0 auto" };
const RAIL: CSSProperties = { display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "8px 4px", paddingRight: "max(4px, env(safe-area-inset-right))", borderLeft: "1px solid var(--qf-border)", background: "var(--qf-surface)", flex: "0 0 auto" };
const KEY: CSSProperties = { position: "relative", width: 44, height: 40, flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid var(--qf-border)", background: "var(--qf-surface)", color: "var(--qf-text)", font: "inherit", cursor: "pointer" };
const KEY_ON: CSSProperties = { background: "var(--qf-accent)", color: "var(--qf-background)", borderColor: "var(--qf-accent)" };
const BADGE: CSSProperties = { position: "absolute", top: -5, right: -5, minWidth: 16, height: 16, padding: "0 4px", borderRadius: 999, background: "var(--qf-accent)", color: "var(--qf-background)", fontSize: 10, fontWeight: 700, lineHeight: "16px", textAlign: "center" };
const SEARCH: CSSProperties = { width: "100%", height: 38, padding: "0 10px", borderRadius: 8, border: "1px solid var(--qf-border)", background: "var(--qf-background)", color: "var(--qf-text)", font: "inherit", fontSize: 16 };
const CHIP_OUT: CSSProperties = { padding: "3px 8px", borderRadius: 999, border: "1px solid var(--qf-border)", fontSize: 13, whiteSpace: "nowrap", color: "var(--qf-text)" };
const CHIP_FILL: CSSProperties = { padding: "3px 8px", borderRadius: 999, background: "var(--qf-selection)", fontSize: 13, whiteSpace: "nowrap", color: "var(--qf-text)" };
const PLUS: CSSProperties = { flex: "0 0 auto", minWidth: 32, height: 28, padding: "0 6px", borderRadius: 6, border: "1px solid var(--qf-border)", background: "var(--qf-surface)", color: "var(--qf-text-secondary)", font: "inherit", fontSize: 12, fontWeight: 600, cursor: "pointer" };
const DROP: CSSProperties = { position: "absolute", top: 44, zIndex: 41, minWidth: 240, maxWidth: "calc(100% - 16px)", maxHeight: "60%", overflowY: "auto", background: "var(--qf-surface)", border: "1px solid var(--qf-border)", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.35)", padding: "4px 0" };
const DROP_HEAD: CSSProperties = { display: "flex", justifyContent: "space-between", gap: 12, padding: "8px 12px", fontWeight: 700, borderBottom: "1px solid var(--qf-border)" };
const DROP_ROW: CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "0 12px", minHeight: 40 };
const DROP_LINK: CSSProperties = { display: "block", width: "100%", textAlign: "left", padding: "0 12px", minHeight: 44, border: 0, borderTop: "1px solid var(--qf-border)", background: "none", color: "var(--qf-accent)", font: "inherit", cursor: "pointer" };
const X: CSSProperties = { width: 36, height: 36, borderRadius: 8, border: 0, background: "none", color: "var(--qf-text-secondary)", font: "inherit", fontSize: 16, cursor: "pointer" };
const SCRIM: CSSProperties = { position: "fixed", inset: 0, zIndex: 60, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "flex-end", justifyContent: "center" };
const SHEET: CSSProperties = { width: "min(100%, 520px)", maxHeight: "85%", display: "flex", flexDirection: "column", background: "var(--qf-surface)", borderRadius: "14px 14px 0 0", paddingBottom: "env(safe-area-inset-bottom)", color: "var(--qf-text)" };
const SHEET_HEAD: CSSProperties = { display: "flex", alignItems: "center", gap: 8, padding: "10px 8px 6px 14px" };
const SHEET_FOOT: CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "10px 14px", borderTop: "1px solid var(--qf-border)" };
const SECTION: CSSProperties = { display: "flex", alignItems: "center", gap: 6, padding: "6px 0" };
const SECTION_K: CSSProperties = { width: 64, flex: "0 0 auto", color: "var(--qf-text-muted)", fontSize: 13 };
const PILL: CSSProperties = { minHeight: 32, padding: "0 12px", borderRadius: 999, border: "1px solid var(--qf-border)", background: "transparent", color: "var(--qf-text)", font: "inherit", fontSize: 13, cursor: "pointer" };
const PILL_ON: CSSProperties = { background: "var(--qf-accent)", color: "var(--qf-background)", borderColor: "var(--qf-accent)" };
const FOLD: CSSProperties = { display: "flex", alignItems: "center", gap: 4, width: "100%", minHeight: 44, padding: 0, border: 0, borderTop: "1px solid var(--qf-border)", background: "none", color: "var(--qf-text)", font: "inherit", fontWeight: 600, cursor: "pointer" };
const TICK: CSSProperties = { display: "flex", alignItems: "center", gap: 8, width: "100%", minHeight: 44, padding: "0 4px 0 20px", border: 0, background: "none", color: "var(--qf-text)", font: "inherit", fontSize: 15, cursor: "pointer" };
const MUTED: CSSProperties = { color: "var(--qf-text-muted)" };
